import React, { useCallback, useEffect, useMemo, useRef } from "react";
import {
  buildReadingContentKey,
  clearReadingPosition,
  getReadingPosition,
  saveReadingPosition,
} from "../services/readingPosition";

export const READING_POSITION_SAVE_INTERVAL_MS = 250;

export interface UseReadingPositionOptions {
  userId: string;
  sectionId: string;
  adjustedReadingSectionId?: string;
  adjustedText: string;
  layoutKey: string;
}

export interface UseReadingPositionResult {
  adjustedScrollRef: React.RefCallback<HTMLDivElement>;
  handleAdjustedScroll: React.UIEventHandler<HTMLDivElement>;
  adjustedScrollKey: string;
  clearAdjustedReadingPosition: () => void;
}

interface ActiveScrollContainer {
  element: HTMLDivElement;
  userId: string;
  sectionId: string;
  contentKey: string;
  identityKey: string;
  latestProgress: number | null;
  restoring: boolean;
  restorationFrame: number | null;
  restorationCompleteFrame: number | null;
  pendingSaveTimer: ReturnType<typeof setTimeout> | null;
  attached: boolean;
  writesSuppressed: boolean;
}

const measure = (element: HTMLDivElement): number | null => {
  const maxScroll = element.scrollHeight - element.clientHeight;
  return maxScroll > 0 ? element.scrollTop / maxScroll : null;
};

const persist = (
  association: ActiveScrollContainer,
  progress: number | null,
): void => {
  if (progress === null || association.writesSuppressed) {
    return;
  }

  saveReadingPosition(
    association.userId,
    association.sectionId,
    association.contentKey,
    progress,
  );
};

const cancelPendingSave = (association: ActiveScrollContainer): void => {
  if (association.pendingSaveTimer !== null) {
    clearTimeout(association.pendingSaveTimer);
    association.pendingSaveTimer = null;
  }
};

const cancelRestoration = (association: ActiveScrollContainer): void => {
  if (association.restorationFrame !== null) {
    window.cancelAnimationFrame(association.restorationFrame);
    association.restorationFrame = null;
  }
  if (association.restorationCompleteFrame !== null) {
    window.cancelAnimationFrame(association.restorationCompleteFrame);
    association.restorationCompleteFrame = null;
  }
};

const flush = (association: ActiveScrollContainer): void => {
  cancelPendingSave(association);
  if (association.restoring || association.writesSuppressed) {
    return;
  }

  if (association.attached) {
    association.latestProgress = measure(association.element);
  }
  persist(association, association.latestProgress);
};

export function useReadingPosition({
  userId,
  sectionId,
  adjustedReadingSectionId,
  adjustedText,
  layoutKey,
}: UseReadingPositionOptions): UseReadingPositionResult {
  const contentKey = useMemo(
    () => buildReadingContentKey(adjustedReadingSectionId, adjustedText),
    [adjustedReadingSectionId, adjustedText],
  );
  const identityKey = `${encodeURIComponent(userId)}:${encodeURIComponent(
    sectionId,
  )}:${encodeURIComponent(contentKey)}`;
  const activeScrollContainerRef = useRef<ActiveScrollContainer | null>(null);
  const suppressedIdentityKeysRef = useRef(new Set<string>());
  const adjustedScrollRef = useCallback(
    (element: HTMLDivElement | null) => {
      const previousAssociation = activeScrollContainerRef.current;

      if (previousAssociation) {
        if (!element && previousAssociation.identityKey !== identityKey) {
          return;
        }
        flush(previousAssociation);
        cancelRestoration(previousAssociation);
        previousAssociation.attached = false;
        activeScrollContainerRef.current = null;
      }

      if (!element) {
        return;
      }

      const association: ActiveScrollContainer = {
        element,
        userId,
        sectionId,
        contentKey,
        identityKey,
        latestProgress: null,
        restoring: true,
        restorationFrame: null,
        restorationCompleteFrame: null,
        pendingSaveTimer: null,
        attached: true,
        writesSuppressed: suppressedIdentityKeysRef.current.has(identityKey),
      };
      activeScrollContainerRef.current = association;
      association.restorationFrame = window.requestAnimationFrame(() => {
        association.restorationFrame = null;
        if (activeScrollContainerRef.current !== association) {
          return;
        }

        const progress = getReadingPosition(
          association.userId,
          association.sectionId,
          association.contentKey,
        );
        const maxScroll =
          association.element.scrollHeight - association.element.clientHeight;
        association.element.scrollTop =
          maxScroll > 0 && progress !== null ? progress * maxScroll : 0;
        association.latestProgress = maxScroll > 0 ? progress : null;
        association.restorationCompleteFrame = window.requestAnimationFrame(
          () => {
            association.restorationCompleteFrame = null;
            if (activeScrollContainerRef.current === association) {
              association.restoring = false;
            }
          },
        );
      });
    },
    [contentKey, identityKey, sectionId, userId],
  );
  const handleAdjustedScroll = useCallback(
    (event: React.UIEvent<HTMLDivElement>) => {
      const association = activeScrollContainerRef.current;
      if (
        !association ||
        association.restoring ||
        association.writesSuppressed ||
        association.element !== event.currentTarget
      ) {
        return;
      }

      association.latestProgress = measure(event.currentTarget);
      if (association.pendingSaveTimer !== null) {
        return;
      }

      const timer = setTimeout(() => {
        if (association.pendingSaveTimer !== timer) {
          return;
        }
        association.pendingSaveTimer = null;
        persist(association, association.latestProgress);
      }, READING_POSITION_SAVE_INTERVAL_MS);
      association.pendingSaveTimer = timer;
    },
    [],
  );
  useEffect(() => {
    const flushCurrentAssociation = () => {
      const association = activeScrollContainerRef.current;
      if (association) {
        flush(association);
      }
    };
    const handleVisibilityChange = () => {
      if (document.visibilityState === "hidden") {
        flushCurrentAssociation();
      }
    };

    window.addEventListener("pagehide", flushCurrentAssociation);
    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      const association = activeScrollContainerRef.current;
      if (association) {
        flush(association);
        cancelRestoration(association);
        association.attached = false;
        activeScrollContainerRef.current = null;
      }
      window.removeEventListener("pagehide", flushCurrentAssociation);
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, []);
  const clearAdjustedReadingPosition = useCallback(() => {
    suppressedIdentityKeysRef.current.add(identityKey);
    const association = activeScrollContainerRef.current;
    if (association?.identityKey === identityKey) {
      association.writesSuppressed = true;
      association.latestProgress = null;
      cancelPendingSave(association);
      cancelRestoration(association);
    }
    clearReadingPosition(userId, sectionId);
  }, [identityKey, sectionId, userId]);

  return {
    adjustedScrollRef,
    handleAdjustedScroll,
    adjustedScrollKey: `${identityKey}:${layoutKey}`,
    clearAdjustedReadingPosition,
  };
}
